import React, { useEffect, useMemo, useState } from "react";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLock, faLockOpen } from "@fortawesome/free-solid-svg-icons";

const VoucherReceiving = () => {
  const [receiptData, setReceiptData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  const centerCode = localStorage.getItem("code");
  const loggedSewadarOf = localStorage.getItem("sewadarOf");
  const isOfficeUser = loggedSewadarOf == "Office";
  const sewadarId = localStorage.getItem('sewadarId');


  const fetchVouchers = async () => {
    try {
      setLoading(true);

      const res = await apiClient.get(
        `${SERVER_BASE}/getReceiptVouchers/${centerCode}`
      );

      const vouchers = (res.data || [])
  .filter((v) => v.receiptStatus == "Final")
  .map((v) => ({
    ...v,
    _rowId: v.id || v.documentNo,
    selected: false,
    amount: v.amount || "",
    isEditing: !v.amount, // ✅ if no amount → editable
  }));

    

      setReceiptData(vouchers);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch receipt vouchers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, []);
  const toggleAmountLock = (rowId) => {
    setReceiptData((prev) =>
      prev.map((row) =>
        row._rowId == rowId
          ? { ...row, isAmountUnlocked: !row.isAmountUnlocked }
          : row
      )
    );
  };
  
  
  const departmentOptions = useMemo(() => {
    const set = new Set();
  
    (receiptData || []).forEach((receipt) => {
      (receipt.items || []).forEach((item) => {
        if (item.departmentName) {
          set.add(item.departmentName);
        }
      });
    });
  
    return Array.from(set);
  }, [receiptData]);
  
  

  /* ---------------- FILTER DATA ---------------- */
  const filteredData = useMemo(() => {
    if (!departmentFilter) return receiptData;
  
    return receiptData.filter((receipt) =>
      (receipt.items || []).some(
        (item) => item.departmentName == departmentFilter
      )
    );
  }, [receiptData, departmentFilter]);
  

  const toggleRow = (rowId) => {
    setReceiptData((prev) =>
      prev.map((row) =>
        row._rowId == rowId
          ? { ...row, selected: !row.selected }
          : row
      )
    );
  };

  const handleAmountChange = (rowId, value) => {
    setReceiptData((prev) =>
      prev.map((row) =>
        row._rowId == rowId
          ? { ...row, amount: value }
          : row
      )
    );
  };

  // const handleSaveAmountSubmit = async (row) => {
  //   try {
  //     if (!row.amount) {
  //       Swal.fire("Error", "Please enter valid amount", "error");
  //       return;
  //     }
  
  //     const payload = {
  //       receiptVoucherId: row.id,
  //       amount: row.amount || null,
  //       submitter: sewadarId
  //     };
  
  //     const res = await apiClient.post(
  //       `${SERVER_BASE}/saveReceiptVoucherAmount`,
  //       payload
  //     );
  
  //      Swal.fire({
  //               icon: "success",
  //               text: res.data.message,
  //               toast: true,
  //               timer: 1500,
  //               showConfirmButton: false,
  //               position: "top-end",
  //             });
  //   } catch (error) {
  //     console.error(error);

  //   }
  // };
 
 
  const handleSaveAmountSubmit = async (row) => {
    try {
      if (!row.amount) {
        Swal.fire("Error", "Please enter valid amount", "error");
        return;
      }
  
      const payload = {
        receiptVoucherId: row.id,
        amount: row.amount || null,
        submitter: sewadarId,
      };
  
      const res = await apiClient.post(
        `${SERVER_BASE}/saveReceiptVoucherAmount`,
        payload
      );
  
      Swal.fire({
        icon: "success",
        text: res.data.message,
        toast: true,
        timer: 1500,
        showConfirmButton: false,
        position: "top-end",
      });
  
      // ✅ THIS WAS MISSING
      setReceiptData((prev) =>
        prev.map((r) =>
          r._rowId === row._rowId
            ? { ...r, isEditing: false } // 🔒 disable immediately
            : r
        )
      );
    } catch (error) {
      console.error(error);
    }
  };
  
  const selectableRows = receiptData.filter(
    r => r.amount && !r.receivedBy
  );
  

  const handleSaveSelected = async () => {
    const selectedRows = receiptData.filter(
      (row) => row.selected && Number(row.amount) >= 0
    );
  
    if (!selectedRows.length) {
      Swal.fire("Warning", "No vouchers selected", "warning");
      return;
    }
  
    try {
      const payload = {
        receiptVoucherIds: selectedRows.map(r => r.id),
        receivedBy: sewadarId,
      };
  
      const res = await apiClient.post(
        `${SERVER_BASE}/markReceiptVouchersReceived`,
        payload
      );
  
      Swal.fire("Success", res.data.message, "success");
  
      // refresh list
      fetchVouchers();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to mark received", "error");
    }
  };
  
  const allSelected =
  selectableRows.length > 0 &&
  selectableRows.every(r => r.selected);

  
  const columns = useMemo(() => {
    const cols = [
      {
        Header: "S.No",
        disableSortBy: true,
        Cell: ({ row }) => row.index + 1,
      },
      
      { Header: "Ledger Name", accessor: "ledgerName" },
      { Header: "Document No", accessor: "documentNo" },
      {
        Header: "Document Date",
        accessor: "documentDate",
        Cell: ({ value }) =>
          value ? moment(value).format("DD MMM YYYY") : "",
      },
      {
        Header: "Department",
        Cell: ({ row }) => {
          const departments = (row.original.items || [])
            .map((item) => item.departmentName)
            .filter(Boolean);
      
          // remove duplicates
          const uniqueDepartments = [...new Set(departments)];
      
          return uniqueDepartments.join(", ");
        },
      },
    
      {
        Header: "Amount",
        disableSortBy: true,
        Cell: ({ row }) => {
          const isDisabled =
            isOfficeUser || !row.original.isEditing;
      
          return (
            <input
              type="number"
              className="form-control form-control-sm"
              value={row.original.amount ?? ""}
              disabled={isDisabled}
              onChange={(e) =>
                handleAmountChange(
                  row.original._rowId,
                  e.target.value
                )
              }
            />
          );
        },
      },
      
      {
        Header: "Received By",
        Cell: ({ row }) => {
          const firstName = row?.original?.receivedBySewadar?.firstName || "";
          const lastName = row?.original?.receivedBySewadar?.lastName || "";
          const receivedAt = row?.original?.receivedAt;
      
          const fullName = [firstName, lastName].filter(Boolean).join(" ");
      
          if (!fullName) return "";
      
          return (
            <div>
              <div>{fullName}</div>
              {receivedAt && (
                <small className="text-muted">
                  {moment(receivedAt).format("DD MMM YYYY, hh:mm A")}
                </small>
              )}
            </div>
          );
        },
      },
      
      
      
    ];
    if (!isOfficeUser) {
      cols.push({
        Header: "Action",
        disableSortBy: true,
        Cell: ({ row }) => {
          const isEditing = row.original.isEditing;
          const rowId = row.original._rowId;
    
          return (
            <button
              type="button"
              className={`btn btn-sm ${
                isEditing ? "btn-success" : "btn-warning"
              }`}
              onClick={() => {
                setReceiptData((prev) =>
                  prev.map((r) => {
                    if (r._rowId !== rowId) return r;
    
                    // 🔓 LOCK CLICK → ENABLE EDIT
                    if (!r.isEditing) {
                      return { ...r, isEditing: true };
                    }
    
                    // ✅ SAVE CLICK
                    handleSaveAmountSubmit(r);
                    return r;
                  })
                );
              }}
            >
              <FontAwesomeIcon icon={isEditing ? faCheck : faLock} />
            </button>
          );
        },
      });
    }
    
    
    

    if (isOfficeUser) {
      cols.push({
        id: "select",
        Header: () => (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => {
              const checked = e.target.checked;
              setReceiptData(prev =>
                prev.map(r =>
                  r.amount && !r.receivedBy
                    ? { ...r, selected: checked }
                    : { ...r, selected: false } // force reset
                )
              );              
            }}
          />
        ),
        disableSortBy: true,
        Cell: ({ row }) =>
          (row.original.amount && !row.original.receivedBy) ? (
            <input
              type="checkbox"
              checked={row.original.selected}
              onChange={() => toggleRow(row.original._rowId)}
            />
          ) : null,   // 🚫 NO CHECKBOX IF NO AMOUNT
      });
    }
    
  
    return cols;
  }, [ isOfficeUser,allSelected]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aHasAmount = !!a.amount;
      const bHasAmount = !!b.amount;
  
      if (isOfficeUser) {
        return Number(bHasAmount) - Number(aHasAmount);
      } else {
        return Number(aHasAmount) - Number(bHasAmount);
      }
    });
  }, [filteredData, isOfficeUser]);
  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex, pageSize },
    setPageSize,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: 25 },
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter(searchText || undefined);
  }, [searchText, setGlobalFilter]);



  return (
    <div className="formcontainer mt-5">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5>Voucher Receiving</h5>
        <small className="text-muted">
          Showing <strong>{rows.length}</strong> of{" "}
          <strong>{filteredData.length}</strong> vouchers
        </small>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <input
          type="text"
          className="form-control form-control-sm w-50"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <select
          className="form-select form-select-sm w-25"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
        >
          <option value="">All Departments</option>
          {departmentOptions.map((dep) => (
            <option key={dep} value={dep}>
              {dep}
            </option>
          ))}
        </select>

        <select
          className="form-select form-select-sm w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[25, 50, 100, 200].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>

        {isOfficeUser && (
          <button
            className="btn btn-success btn-sm ms-auto"
            onClick={handleSaveSelected}
            disabled={loading}
          >
            Mark as received
          </button>
        )}
      </div>

      {/* TABLE */}
      <table className="table table-bordered table-sm" {...getTableProps()}>
        <thead className="table-light">
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}
                  {col.isSorted
                    ? col.isSortedDesc
                      ? " 🔽"
                      : " 🔼"
                    : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of{" "}
          <strong>{pageOptions.length}</strong>
        </span>

        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={nextPage}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default VoucherReceiving;
